import { FEATURE_DEFINITIONS } from '../central/constants/feature-access.config';
import type { CompanyPackagesDTO } from '../central/dto/company';
import type { FeatureType } from '../central/types/feature-access.type';
import type { PackageKeys } from '../central/types/package.type';

export const getActivePackages = (
  companyPackages?: CompanyPackagesDTO,
): PackageKeys[] => {
  if (!companyPackages) {
    return [];
  }

  return Object.entries(companyPackages)
    .filter(([, pkg]) => {
      if (!pkg.isActive) return false;

      // Date validation logic
      if (pkg.startDate && pkg.endDate) {
        // Parse and zero out the time for all dates
        const now = new Date();
        const startDate = new Date(
          pkg.startDate.split('-').reverse().join('-'),
        );
        const endDate = new Date(pkg.endDate.split('-').reverse().join('-'));

        const nowDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const startDateOnly = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
        );
        const endDateOnly = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
        );

        // Compare only the date part
        return (
          nowDate.getTime() >= startDateOnly.getTime() &&
          nowDate.getTime() <= endDateOnly.getTime()
        );
      }

      return true;
    })
    .map(([key]) => key as PackageKeys);
};

export const hasFeatureAccess = (
  companyPackages: CompanyPackagesDTO,
  feature: FeatureType,
): boolean => {
  if (!companyPackages) {
    return false;
  }

  const featureDefinition = FEATURE_DEFINITIONS.find(
    (featureDef) => featureDef.id === feature,
  );

  if (!featureDefinition) {
    return false;
  }

  // If feature is not package-gated, it's always available
  if (!featureDefinition.isPackageGated) {
    return true;
  }

  const activePackages = getActivePackages(companyPackages);

  // Check if any active package includes this feature
  return activePackages.some((packageKey) =>
    featureDefinition.availableInPackages.includes(packageKey),
  );
};

export const getFeatureDefinition = (feature: FeatureType) => {
  return FEATURE_DEFINITIONS.find((featureDef) => featureDef.id === feature);
};

/**
 * Get all available features for a company
 */
export const getAvailableFeatures = async (
  companyPackages: CompanyPackagesDTO,
): Promise<FeatureType[]> => {
  const availableFeatures: FeatureType[] = [];

  for (const featureDefinition of FEATURE_DEFINITIONS) {
    const hasAccess = await hasFeatureAccess(
      companyPackages,
      featureDefinition.id,
    );
    if (hasAccess) {
      availableFeatures.push(featureDefinition.id);
    }
  }

  return availableFeatures;
};
